import { z } from "zod";
import { fileUrl } from "./common";
import { questionnaireAnswersSchema } from "./enrollment-questionnaire";
import { LANGUAGE_CODES, BOSNIAN_CODE, MAX_ADDITIONAL_LANGUAGES } from "@/lib/languages";
import { BANK_ACCOUNT_ERROR, isValidBankAccount, normalizeBankAccount } from "@/lib/bank-account";
import { PHONE_ERROR, isValidPhone } from "@/lib/phone";
import { parseVideoEmbed } from "@/lib/video-embed";

/** Najduži dozvoljeni opis profila (bio), isti za prijavu, postavke i brojač u formama. */
export const THERAPIST_BIO_MAX_LENGTH = 10000;

const taxIdSchema = z.string().trim().regex(/^\d{13}$/, "Mora biti tačno 13 cifara");

// Račun za isplatu: važi za oba načina isplate (obrt i ugovor o djelu), jer je to
// račun na koji novac ide. Sprema se normalizovan (bez razmaka i crtica, velika slova).
export const bankAccountSchema = z
  .string()
  .transform(normalizeBankAccount)
  .refine(isValidBankAccount, BANK_ACCOUNT_ERROR);
// Telefon: obavezan pri prijavi. U postavkama prazan string znači "nije unesen"
// (WP-migrirani profili imaju phone = ""), pa ne smije blokirati spremanje profila.
const phoneSchema = z.string().trim().max(30).refine(isValidPhone, PHONE_ERROR);
const optionalPhoneSchema = z.string().trim().max(30).refine((v) => v === "" || isValidPhone(v), PHONE_ERROR);

export const bankNameSchema = z.string().trim().min(2, "Unesi naziv banke").max(120);

// Specijalizacije: ista pravila za prijavu i postavke. Poruke ("Specijalizacije:
// Potrebno je odabrati bar jednu stavku.") slaže formatValidationError.
export const MAX_SPECIALIZATIONS = 20;
const specializationsSchema = z.array(z.string().trim().min(1)).min(1).max(MAX_SPECIALIZATIONS);

// Intro video: fajl uploadovan na platformu ili YouTube/Vimeo link koji
// parseVideoEmbed prepoznaje. Javni profil link prikazuje samo kroz normalizovan
// embed URL, pa se drugi linkovi (koje <video> ne može pustiti) odbijaju odmah.
export const PROFILE_VIDEO_ERROR =
  "Intro video mora biti link na YouTube ili Vimeo video, npr. https://www.youtube.com/watch?v=...";
export function isValidProfileVideoUrl(value: string): boolean {
  return value.startsWith("/uploads/") || parseVideoEmbed(value) !== null;
}
const profileVideoUrlSchema = z.string().trim().max(500).refine(isValidProfileVideoUrl, PROFILE_VIDEO_ERROR);

// Podaci za isplatu: tekst se trimuje, pa "  " ne prolazi kao popunjeno polje.
const billingText = (max: number) => z.string().trim().min(1).max(max);
const vatIdSchema = z.string().trim().max(20);

const languagesArraySchema = z
  .array(z.enum(LANGUAGE_CODES as unknown as [string, ...string[]]))
  .max(MAX_ADDITIONAL_LANGUAGES)
  .refine((arr) => !arr.includes(BOSNIAN_CODE), {
    message: "Bosanski se dodaje automatski",
  })
  .refine((arr) => new Set(arr).size === arr.length, {
    message: "Duplikat jezika",
  });

export const enrollTherapistSchema = z
  .object({
    // Full name (ime i prezime) — written to user.name on enrollment.
    fullName: z.string().trim().min(2).max(120),
    bio: z.string().min(10).max(THERAPIST_BIO_MAX_LENGTH),
    specializations: specializationsSchema,
    licenseNumber: z.string().max(100).optional().default(""),
    yearsExperience: z.number().int().min(0).max(70).optional().default(0),
    phone: phoneSchema,
    payoutMethod: z.enum(["invoice", "work_contract"]),
    // Working mode shown on the public profile (online / in-person / both).
    sessionModality: z.enum(["online", "in_person", "both"]).optional().default("online"),
    profileImageUrl: fileUrl,
    profileVideoUrl: profileVideoUrlSchema.optional(),
    documents: z
      .array(
        z.object({
          name: z.string().min(1).max(200),
          fileUrl: fileUrl,
          fileType: z.enum(["pdf", "image"]),
        })
      )
      .min(1)
      .max(10),
    isVolunteer: z.boolean().optional().default(false),
    languages: languagesArraySchema.optional().default([]),
    questionnaireAnswers: questionnaireAnswersSchema.optional(),
    // Billing identity (conditional on payoutMethod)
    businessName: billingText(200).optional(),
    businessAddress: billingText(400).optional(),
    officeAddress: billingText(400).optional(),
    businessTaxId: taxIdSchema.optional(),
    businessVatId: vatIdSchema.optional(),
    personalAddress: billingText(400).optional(),
    personalTaxId: taxIdSchema.optional(),
    // Račun za isplatu (oba načina isplate)
    bankName: bankNameSchema.optional(),
    bankAccount: bankAccountSchema.optional(),
  })
  .superRefine((d, ctx) => {
    if (d.isVolunteer) return;
    if (d.payoutMethod === "invoice") {
      if (!d.businessName) ctx.addIssue({ code: "custom", path: ["businessName"], message: "Naziv firme je obavezan" });
      if (!d.businessAddress) ctx.addIssue({ code: "custom", path: ["businessAddress"], message: "Adresa firme je obavezna" });
      if (!d.businessTaxId) ctx.addIssue({ code: "custom", path: ["businessTaxId"], message: "JIB je obavezan" });
    } else {
      if (!d.personalAddress) ctx.addIssue({ code: "custom", path: ["personalAddress"], message: "Adresa je obavezna" });
      if (!d.personalTaxId) ctx.addIssue({ code: "custom", path: ["personalTaxId"], message: "JMBG je obavezan" });
    }
    if (!d.bankName) ctx.addIssue({ code: "custom", path: ["bankName"], message: "Naziv banke je obavezan" });
    if (!d.bankAccount) ctx.addIssue({ code: "custom", path: ["bankAccount"], message: "Broj računa je obavezan" });
  });

// Onboarding step for migrated therapists: confirm payout method, fill billing
// identity, and accept the platform terms. Conditional required-field rules
// (invoice vs work_contract, volunteer skip) are enforced in the handler, which
// knows the profile's isVolunteer flag.
export const billingTermsSchema = z.object({
  payoutMethod: z.enum(["invoice", "work_contract"]),
  agreedToTerms: z.literal(true),
  businessName: billingText(200).optional(),
  businessAddress: billingText(400).optional(),
  businessTaxId: taxIdSchema.optional(),
  businessVatId: vatIdSchema.optional(),
  personalAddress: billingText(400).optional(),
  personalTaxId: taxIdSchema.optional(),
  bankName: bankNameSchema.optional(),
  bankAccount: bankAccountSchema.optional(),
});

export const updateTherapistSchema = z.object({
  // Full name (ime i prezime) — stored on the `user` table, not therapist_profiles.
  // Single source of truth for the name shown in admin lists, the subscription
  // ledger, and the receipt PDF (snapshotted at payment-link generation).
  fullName: z.string().trim().min(2).max(120).optional(),
  bio: z.string().min(10).max(THERAPIST_BIO_MAX_LENGTH).optional(),
  specializations: specializationsSchema.optional(),
  licenseNumber: z.string().max(100).optional(),
  yearsExperience: z.number().int().min(0).max(70).optional(),
  phone: optionalPhoneSchema.optional(),
  sessionModality: z.enum(["online", "in_person", "both"]).optional(),
  profileImageUrl: fileUrl.optional(),
  // Prazno polje u postavkama briše video (null).
  profileVideoUrl: z
    .preprocess((v) => (typeof v === "string" && v.trim() === "" ? null : v), profileVideoUrlSchema.nullable())
    .optional(),
  isVolunteer: z.boolean().optional(),
  languages: languagesArraySchema.optional(),
  // Payout method decides which billing identity the subscription receipt uses.
  // Editable after enrollment so a therapist (or admin) can correct a wrong
  // choice; per-field completeness is enforced at the payment step, not here.
  payoutMethod: z.enum(["invoice", "work_contract"]).optional(),
  // Billing identity — therapist self-edit (used to generate subscription payment links)
  businessName: billingText(200).optional(),
  businessAddress: billingText(400).optional(),
  officeAddress: z.string().max(400).optional(),
  businessTaxId: taxIdSchema.optional(),
  businessVatId: vatIdSchema.optional(),
  personalAddress: billingText(400).optional(),
  personalTaxId: taxIdSchema.optional(),
  bankName: bankNameSchema.optional(),
  bankAccount: bankAccountSchema.optional(),
});

export const updateTherapistStatusSchema = z.object({
  status: z.enum(["approved", "rejected"]),
  rejectionReason: z.string().max(1000).optional(),
});

export const updateTherapistActivationSchema = z
  .object({
    active: z.boolean(),
    reason: z.string().max(1000).optional(),
    cancelFutureSessions: z.boolean().optional(),
  })
  .strict();
